import { TEST_ID_INVITATION } from "e2e/testId";
import { AlertCircleIcon } from "lucide-react-native";
import React, { memo } from "react";
import {
  Control,
  Controller,
  ControllerRenderProps,
  FieldErrors,
} from "react-hook-form";
import { Platform } from "react-native";

import CustomSelect from "@/components/molecules/CustomSelect";
import { Button } from "@/components/atoms";
import {
  FormControl,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
  FormControlLabel,
  FormControlLabelText,
} from "@/components/atoms/FormControl";
import { Input } from "@/components/atoms/Input";
import { KeyboardAvoidingView } from "@/components/atoms/KeyboardAvoidingView";
import { ScrollView } from "@/components/atoms/ScrollView";
import { Stack } from "@/components/atoms/Stack";

/**
 * Props for the CreateInvitationView component.
 */
interface Props {
  isValid: boolean;
  loading: boolean;
  widthContent: number;
  onSubmit: () => void;
  control: Control<IForm>;
  errors: FieldErrors<IForm>;
  keyboardVerticalOffset?: number;
}

/**
 * Form data interface for creating an invitation.
 */
export interface IForm {
  phoneNumber: string;
  callingCode: string;
  roleName: "MEMBER" | "MANAGER" | "OWNER";
}
const options = [
  { label: "Member", value: "MEMBER" },
  { label: "Manager", value: "MANAGER" },
  { label: "Owner", value: "OWNER" },
];

/**
 * Factory function for rendering calling code input field
 * @param loading - Whether the form is in a loading state
 * @param errors - Form validation errors object
 */
const renderCallingCodeField = (loading: boolean, errors: FieldErrors<IForm>) =>
  function CallingCodeField({
    field: { onChange, onBlur, value },
  }: {
    field: ControllerRenderProps<IForm, "callingCode">;
  }) {
    return (
      <FormControl
        size="md"
        isDisabled={false}
        isInvalid={!loading && !!errors.callingCode}
        isReadOnly={false}
        isRequired={false}
      >
        <FormControlLabel className="pb-1">
          <FormControlLabelText>Country Code</FormControlLabelText>
        </FormControlLabel>
        <Input
          variant="outline"
          className="border-2"
          size="xl"
          field={{
            placeholder: "Country Code",
            type: "text",
            value: value ?? "",
            keyboardType: "phone-pad",
            inputMode: "tel",
            autoCorrect: false,
            autoComplete: "tel-device",
            textContentType: "telephoneNumber",
            autoCapitalize: "none",
            returnKeyLabel: "Next",
            enterKeyHint: "next",
            onChangeText: onChange,
            onBlur,
            className: "font-body",
          }}
        />

        <FormControlError>
          <FormControlErrorIcon as={AlertCircleIcon} />
          <FormControlErrorText className="shrink">
            {errors.callingCode?.message}
          </FormControlErrorText>
        </FormControlError>
      </FormControl>
    );
  };

/**
 * Factory function for rendering phone number input field
 * @param loading - Whether the form is in a loading state
 * @param errors - Form validation errors object
 * @param onSubmit - Callback function for form submission
 */
const renderPhoneNumberField = (
  loading: boolean,
  errors: FieldErrors<IForm>,
  onSubmit: () => void
) =>
  function PhoneNumberField({
    field: { onChange, onBlur, value },
  }: {
    field: ControllerRenderProps<IForm, "phoneNumber">;
  }) {
    return (
      <FormControl
        size="md"
        isDisabled={false}
        isInvalid={!loading && !!errors.phoneNumber}
        isReadOnly={false}
        isRequired={false}
      >
        <FormControlLabel className="pb-1">
          <FormControlLabelText>Phone Number</FormControlLabelText>
        </FormControlLabel>
        <Input
          variant="outline"
          className="border-2"
          size="xl"
          field={{
            testID: TEST_ID_INVITATION.PHONE_INPUT,
            placeholder: "5555555555",
            type: "text",
            value: value ?? "",
            keyboardType: "phone-pad",
            inputMode: "tel",
            autoCorrect: false,
            autoComplete: "tel-device",
            textContentType: "telephoneNumber",
            autoCapitalize: "none",
            returnKeyLabel: "Next",
            enterKeyHint: "next",
            onChangeText: onChange,
            onBlur,
            onSubmitEditing: onSubmit,
            className: "font-body",
          }}
        />

        <FormControlError>
          <FormControlErrorIcon as={AlertCircleIcon} />
          <FormControlErrorText className="shrink">
            {errors.phoneNumber?.message}
          </FormControlErrorText>
        </FormControlError>
      </FormControl>
    );
  };

/**
 * Factory function for rendering role name selection field
 * @param loading - Whether the form is in a loading state
 * @param errors - Form validation errors object
 */
const renderRoleNameField = (loading: boolean, errors: FieldErrors<IForm>) =>
  function RoleNameField({
    field: { onChange, value },
  }: {
    field: ControllerRenderProps<IForm, "roleName">;
  }) {
    return (
      <FormControl
        size="md"
        isDisabled={false}
        isInvalid={!loading && !!errors.roleName}
        isReadOnly={false}
      >
        <FormControlLabel className="pb-1">
          <FormControlLabelText>Choose Role</FormControlLabelText>
        </FormControlLabel>
        <CustomSelect
          options={options}
          defaultValue={value}
          placeholder="Role"
          onChangeValue={onChange}
        />
        <FormControlError>
          <FormControlErrorIcon as={AlertCircleIcon} />
          <FormControlErrorText className="shrink">
            {errors.roleName?.message}
          </FormControlErrorText>
        </FormControlError>
      </FormControl>
    );
  };

/**
 * View component for displaying the create invitation form with phone number and role selection.
 * @param root0 - The component props
 * @param root0.isValid - Boolean indicating if all form fields are valid
 * @param root0.onSubmit - Callback function to handle form submission
 * @param root0.control - React Hook Form control object for managing form state
 * @param root0.errors - Object containing validation errors for each form field
 * @param root0.loading - Boolean indicating if invitation creation is in progress
 * @param root0.widthContent - The content width for responsive layout sizing
 * @param root0.keyboardVerticalOffset - Optional vertical offset for keyboard avoidance behavior
 */
const CreateInvitationView = ({
  isValid,
  onSubmit,
  control,
  errors,
  loading,
  widthContent,
  keyboardVerticalOffset,
}: Props) => (
  <KeyboardAvoidingView
    behavior={Platform.OS === "ios" ? "padding" : "height"}
    // eslint-disable-next-line no-restricted-syntax -- KeyboardAvoidingView is a bare react-native re-export (no cssInterop for className, no UNSAFE_style forwarding); widthContent is a runtime measured window width off the closed sizing ladder (max token 384px)
    style={{ flex: 1, width: widthContent, alignSelf: "center" }}
    keyboardVerticalOffset={keyboardVerticalOffset}
  >
    <ScrollView
      keyboardShouldPersistTaps="handled"
      className="flex-1"
      contentContainerClassName="grow py-6"
    >
      <Stack className="flex-1 gap-3 px-4">
        <Controller
          name="callingCode"
          control={control}
          render={renderCallingCodeField(loading, errors)}
        />
        <Controller
          name="phoneNumber"
          control={control}
          render={renderPhoneNumberField(loading, errors, onSubmit)}
        />
        <Controller
          name="roleName"
          control={control}
          render={renderRoleNameField(loading, errors)}
        />
      </Stack>
    </ScrollView>
    <Stack className="px-4 pb-4">
      <Button
        testID={TEST_ID_INVITATION.SEND_INVITATION_BUTTON}
        label={!loading ? "Send" : "Please Wait..."}
        isDisabled={loading || !isValid}
        onPress={onSubmit}
        className="bg-surface-action"
        size="lg"
        labelTone="info"
        labelVariant="body"
      />
    </Stack>
  </KeyboardAvoidingView>
);

CreateInvitationView.displayName = "CreateInvitationView";

export default memo(CreateInvitationView);
